/**
 * Document Row Component (List View)
 * Displays a single document in list view with category-colored badges
 */
import React from 'react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { File, Edit, Trash2, Eye, Link2, Ship, Lock } from 'lucide-react';
import { getDocumentTypeLabel, getCategoryForType } from './documentCategories';

const DocumentRow = ({ doc, onEdit, onDelete, onPreview, onLink, onSavePassword, categoryColor }) => {
  const category = getCategoryForType(doc.document_type);
  const isPdf = doc.file_name?.toLowerCase().endsWith('.pdf');

  return (
    <div 
      className="grid grid-cols-12 gap-4 px-4 py-3 items-center hover:bg-slate-700/30 transition-colors cursor-pointer"
      onClick={() => onPreview && onPreview(doc)}
      data-testid={`document-row-${doc.id}`}
    >
      <div className="col-span-4 flex items-center gap-3 min-w-0">
        <File className={`h-4 w-4 ${category.textColor} flex-shrink-0`} />
        <div className="min-w-0">
          <span className="text-white truncate block" title={doc.file_name}>
            {doc.file_name}
          </span>
          {doc.linked_trip && (
            <div className="flex items-center gap-1 mt-0.5">
              <Ship className="h-3 w-3 text-amber-400" />
              <span className="text-xs text-amber-400 truncate">{doc.linked_trip.vessel_name || 'Trip'}</span>
            </div>
          )}
        </div>
      </div>
      <div className="col-span-2">
        <Badge 
          variant="outline" 
          className={`${category.bgColor} ${category.borderColor} ${category.textColor} text-xs`}
        >
          {getDocumentTypeLabel(doc.document_type)}
        </Badge>
      </div>
      <div className="col-span-2 text-sm text-slate-400">
        {doc.tax_year}
      </div>
      <div className="col-span-2 text-sm text-slate-500">
        {new Date(doc.uploaded_at).toLocaleDateString()}
      </div>
      <div className="col-span-2 flex justify-end gap-1">
        <Button
          variant="ghost"
          size="icon"
          onClick={(e) => { e.stopPropagation(); onPreview && onPreview(doc); }}
          className="h-8 w-8 text-slate-400 hover:text-amber-400"
          data-testid={`view-list-document-${doc.id}`}
        >
          <Eye className="h-4 w-4" />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          onClick={(e) => { e.stopPropagation(); onLink && onLink(doc); }}
          className={`h-8 w-8 ${doc.linked_trip_id ? 'text-amber-400' : 'text-slate-400'} hover:text-amber-400`}
          data-testid={`link-list-document-${doc.id}`}
        >
          <Link2 className="h-4 w-4" />
        </Button>
        {isPdf && (
          <Button
            variant="ghost"
            size="icon"
            onClick={(e) => { e.stopPropagation(); onSavePassword && onSavePassword(doc); }}
            className={`h-8 w-8 ${doc.has_saved_password ? 'text-sky-400' : 'text-slate-400 hover:text-amber-400'}`}
            title={doc.has_saved_password ? 'Password saved — click to update' : 'Save PDF password'}
            data-testid={`save-password-list-${doc.id}`}
          >
            <Lock className="h-4 w-4" />
          </Button>
        )}
        <Button
          variant="ghost"
          size="icon"
          onClick={(e) => { e.stopPropagation(); onEdit(doc); }}
          className="h-8 w-8 text-slate-400 hover:text-white"
          data-testid={`edit-list-document-${doc.id}`}
        >
          <Edit className="h-4 w-4" />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          onClick={(e) => { e.stopPropagation(); onDelete(doc.id); }}
          className="h-8 w-8 text-red-400 hover:text-red-300 hover:bg-red-600/10"
          data-testid={`delete-list-document-${doc.id}`}
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
};

export default DocumentRow;
